import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import {
  LayoutDashboard, Shield, Code2, Bug, FileCheck, FileBarChart,
  Settings, LogOut, ChevronLeft, ChevronRight, Sun, Moon, Menu, Search, Bell, Package, CreditCard, ShieldAlert, X, Target, Building2, Coins, Infinity as InfinityIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Popover, PopoverContent, PopoverTrigger
} from '../components/ui/popover';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DARK_LOGO = "https://customer-assets.emergentagent.com/job_vaptara-vuln-ai/artifacts/znyb36lz_vaptara_logo_w.png";
const LIGHT_LOGO = "https://customer-assets.emergentagent.com/job_vaptara-vuln-ai/artifacts/z7et0mch_Vaptara%20logo%20cropped.png";

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/products', icon: Package, label: 'Products' },
  { path: '/engagements', icon: Target, label: 'Engagements' },
  { path: '/scanner', icon: Shield, label: 'Vulnerability Scanner' },
  { path: '/code-scanner', icon: Code2, label: 'Code Scanner' },
  { path: '/vulnerabilities', icon: Bug, label: 'Vulnerabilities' },
  { path: '/security-center', icon: ShieldAlert, label: 'Security Center' },
  { path: '/org-security', icon: Building2, label: 'Organizational Security' },
  { path: '/compliance', icon: FileCheck, label: 'Compliance & GRC' },
  { path: '/reports', icon: FileBarChart, label: 'Reports' },
  { path: '/pricing', icon: CreditCard, label: 'Pricing' },
  { path: '/settings', icon: Settings, label: 'Settings' },
];

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ vulnerabilities: [], scans: [], reports: [] });
  const [searching, setSearching] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(res.data);
      setUnreadCount(res.data.filter(n => !n.read).length);
    } catch {}
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults({ vulnerabilities: [], scans: [], reports: [] });
      return;
    }
    
    setSearching(true);
    try {
      const res = await axios.get(`${API}/search?q=${encodeURIComponent(query)}`, { withCredentials: true });
      setSearchResults(res.data);
    } catch {
      setSearchResults({ vulnerabilities: [], scans: [], reports: [] });
    } finally {
      setSearching(false);
    }
  };

  const markAsRead = async (notifId) => {
    try {
      await axios.put(`${API}/notifications/${notifId}/read`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => n.notif_id === notifId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  const credits = user?.credits;
  const lowCredits = credits && !credits.unlimited && credits.credits_remaining < 20;

  const navigateToResult = (type, id) => {
    setSearchOpen(false);
    setSearchQuery('');
    if (type === 'vulnerability') navigate('/vulnerabilities');
    else if (type === 'scan') navigate('/scanner');
    else if (type === 'report') navigate('/reports');
  };

  return (
    <div className="flex h-[100dvh] w-full overflow-hidden relative" data-testid="dashboard-layout">
      <div className="orb orb-indigo w-[420px] h-[420px] -top-52 right-[-120px] hidden md:block" style={{ opacity: 0.18 }} aria-hidden />
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed md:relative z-40 h-full flex flex-col border-r transition-[width,transform] duration-300 w-[260px] ${
          collapsed ? 'md:w-[68px]' : 'md:w-[260px]'
        } ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
        style={{ background: 'hsl(var(--sidebar-bg))', borderColor: 'hsl(var(--sidebar-border))' }}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="flex items-center h-16 px-4 shrink-0">
          <Link to="/dashboard" className="flex items-center gap-2 overflow-hidden">
            <img
              src={theme === 'dark' ? DARK_LOGO : LIGHT_LOGO}
              alt="Vaptara"
              className={`transition-all duration-300 ${collapsed ? 'h-8 w-8 object-contain object-left' : 'h-9'}`}
              data-testid="logo"
            />
          </Link>
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto hidden md:flex h-7 w-7"
            onClick={() => setCollapsed(!collapsed)}
            data-testid="toggle-sidebar"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>

        <Separator />

        {/* Nav */}
        <ScrollArea className="flex-1 py-3 px-2">
          <nav className="flex flex-col gap-1" data-testid="sidebar-nav">
            {navItems.map(item => {
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={`sidebar-nav-item ${isActive ? 'active' : 'text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--sidebar-hover))]'}`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  title={item.label}
                >
                  <item.icon className={`h-[18px] w-[18px] shrink-0 ${isActive ? 'drop-shadow-[0_0_6px_hsl(var(--primary)/0.6)]' : ''}`} />
                  <span className={`truncate ${collapsed ? 'md:hidden' : ''}`}>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </ScrollArea>

        {/* User section */}
        <div className="p-3 border-t" style={{ borderColor: 'hsl(var(--sidebar-border))' }}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 w-full p-2 rounded-lg hover:bg-accent/50 transition-colors" data-testid="user-menu-trigger">
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-primary/20 text-primary text-xs">{initials}</AvatarFallback>
                </Avatar>
                <div className={`text-left overflow-hidden ${collapsed ? 'md:hidden' : ''}`}>
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.role}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                <Settings className="h-4 w-4 mr-2" /> Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                <LogOut className="h-4 w-4 mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header
          className="h-16 border-b flex items-center justify-between px-4 md:px-6 shrink-0 backdrop-blur-xl relative z-20"
          style={{ background: 'hsl(var(--background) / 0.7)', borderColor: 'hsl(var(--border) / 0.7)' }}
          data-testid="topbar"
        >
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu">
              <Menu className="h-5 w-5" />
            </Button>
            
            {/* Global Search */}
            <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
              <button
                onClick={() => setSearchOpen(true)}
                className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-muted/70 rounded-full w-72 hover:bg-muted transition-colors border border-transparent hover:border-primary/30"
                data-testid="global-search-trigger"
              >
                <Search className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Search vulnerabilities, scans...</span>
                <kbd className="ml-auto text-[10px] bg-background px-1.5 py-0.5 rounded border text-muted-foreground">⌘K</kbd>
              </button>
              <DialogContent className="max-w-2xl p-0">
                <div className="p-4 border-b">
                  <div className="flex items-center gap-3">
                    <Search className="h-5 w-5 text-muted-foreground shrink-0" />
                    <input
                      autoFocus
                      value={searchQuery}
                      onChange={e => handleSearch(e.target.value)}
                      placeholder="Search vulnerabilities, scans, reports..."
                      className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                      data-testid="global-search-input"
                    />
                    {searchQuery && (
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setSearchQuery(''); setSearchResults({ vulnerabilities: [], scans: [], reports: [] }); }}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
                <ScrollArea className="max-h-[400px]">
                  <div className="p-2">
                    {searching && <p className="text-sm text-center text-muted-foreground py-4">Searching...</p>}
                    
                    {!searching && searchQuery.length >= 2 && (
                      <>
                        {searchResults.vulnerabilities?.length > 0 && (
                          <div className="mb-4">
                            <p className="text-xs font-medium text-muted-foreground px-2 mb-2">Vulnerabilities</p>
                            {searchResults.vulnerabilities.map(vuln => (
                              <button
                                key={vuln.vuln_id}
                                onClick={() => navigateToResult('vulnerability', vuln.vuln_id)}
                                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors text-left"
                                data-testid={`search-result-${vuln.vuln_id}`}
                              >
                                <Bug className="h-4 w-4 text-muted-foreground shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{vuln.title}</p>
                                  <p className="text-xs text-muted-foreground truncate">{vuln.asset}</p>
                                </div>
                                <Badge variant="outline" className={`shrink-0 text-xs ${vuln.severity === 'critical' ? 'border-red-500/30 text-red-500' : vuln.severity === 'high' ? 'border-orange-500/30 text-orange-500' : 'border-yellow-500/30 text-yellow-500'}`}>
                                  {vuln.severity}
                                </Badge>
                              </button>
                            ))}
                          </div>
                        )}
                        
                        {searchResults.scans?.length > 0 && (
                          <div className="mb-4">
                            <p className="text-xs font-medium text-muted-foreground px-2 mb-2">Scans</p>
                            {searchResults.scans.map(scan => (
                              <button
                                key={scan.scan_id}
                                onClick={() => navigateToResult('scan', scan.scan_id)}
                                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors text-left"
                              >
                                <Shield className="h-4 w-4 text-muted-foreground shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{scan.scan_type} Scan</p>
                                  <p className="text-xs text-muted-foreground truncate">{scan.target}</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                        
                        {searchResults.reports?.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground px-2 mb-2">Reports</p>
                            {searchResults.reports.map(report => (
                              <button
                                key={report.report_id}
                                onClick={() => navigateToResult('report', report.report_id)}
                                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors text-left"
                              >
                                <FileBarChart className="h-4 w-4 text-muted-foreground shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{report.title}</p>
                                  <p className="text-xs text-muted-foreground truncate">{report.project_name}</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                        
                        {searchResults.vulnerabilities?.length === 0 && searchResults.scans?.length === 0 && searchResults.reports?.length === 0 && (
                          <p className="text-sm text-center text-muted-foreground py-8">No results found for "{searchQuery}"</p>
                        )}
                      </>
                    )}
                    
                    {!searching && searchQuery.length < 2 && (
                      <div className="py-8 text-center text-muted-foreground text-sm">
                        Type at least 2 characters to search
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </DialogContent>
            </Dialog>
          </div>

          <div className="flex items-center gap-2">
            {credits && (
              <button
                onClick={() => navigate('/pricing')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors hover:bg-muted ${lowCredits ? 'border-amber-500/40 text-amber-500 bg-amber-500/10' : 'border-border'}`}
                title="Credits remaining · click to upgrade"
                data-testid="credits-pill"
              >
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-primary/30 text-primary hidden sm:inline-flex" data-testid="plan-badge">{credits.plan_label}</Badge>
                <Coins className="h-3.5 w-3.5" />
                <span data-testid="credits-remaining">{credits.unlimited ? <InfinityIcon className="h-3.5 w-3.5 inline" /> : `${credits.credits_remaining} / ${credits.credits_limit}`}</span>
                <span className="hidden lg:inline text-muted-foreground">credits</span>
              </button>
            )}
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
              {theme === 'dark' ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
            </Button>
            
            {/* Notifications */}
            <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                  <Bell className="h-[18px] w-[18px]" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-destructive rounded-full text-[10px] text-white flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0">
                <div className="flex items-center justify-between p-3 border-b">
                  <h4 className="font-semibold text-sm">Notifications</h4>
                  {unreadCount > 0 && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={markAllAsRead} data-testid="mark-all-read">
                      Mark all read
                    </Button>
                  )}
                </div>
                <ScrollArea className="h-[300px]">
                  <div className="p-2">
                    {notifications.length > 0 ? notifications.map(notif => (
                      <div
                        key={notif.notif_id}
                        className={`p-3 rounded-lg mb-1 cursor-pointer transition-colors ${notif.read ? 'hover:bg-muted' : 'bg-primary/5 hover:bg-primary/10'}`}
                        onClick={() => markAsRead(notif.notif_id)}
                        data-testid={`notif-${notif.notif_id}`}
                      >
                        <div className="flex items-start gap-2">
                          {!notif.read && <div className="h-2 w-2 rounded-full bg-primary mt-1.5 shrink-0" />}
                          <div className={!notif.read ? '' : 'ml-4'}>
                            <p className="text-sm font-medium">{notif.title}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{notif.message}</p>
                            <p className="text-[10px] text-muted-foreground mt-1">{new Date(notif.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                      </div>
                    )) : (
                      <div className="py-8 text-center text-muted-foreground text-sm">
                        <Bell className="h-8 w-8 mx-auto mb-2 opacity-30" />
                        No notifications
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden relative" data-testid="main-content">
          <motion.div key={location.pathname} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }} className="p-4 md:p-6 max-w-[1600px] mx-auto min-w-0">
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
}
